import { TinyCompanyDTO, LabelDTO } from 'types/company';
import config from 'config';
import useResource from 'util/resource/useResource';
import usePaginatedResource from 'util/resource/usePaginatedResource';
import { invalidate, invalidateStartsWith } from 'hooks/useSWR';
import useLazyResource from 'util/resource/useLazyResource';
import { put } from 'apis/ApiBase';
import ApiBase from 'apis/ApiBase';
import useNotify from 'hooks/useNotify';
import { CompactLabelDTO } from 'types';
import { UserProfile, TinyUser } from 'types/user';
import { PaginationOptions, SortOptions } from 'types/api';
import { withPagination } from 'apis/utils';
import { Location } from 'apis/MapboxAPI';
import { applicationUrls as applicationUrls } from './communityApplicationsApi';
import { GroupsCompaniesFilters } from './types';

const baseUrl = config.COMPANIES_API_URL;
const api = ApiBase();

export const groupsUrls = {
  list: (communityId: number) => `${baseUrl}/communities/${communityId}/groups`,
  listCompanies: (
    communityId: number,
    groupId: UUID,
    filters?: GroupsCompaniesFilters,
    sortOptions?: SortOptions,
    paginationOptions?: PaginationOptions,
  ) =>
    withPagination(`${baseUrl}/communities/${communityId}/groups/${groupId}/companies`, paginationOptions, {
      sortBy: sortOptions?.sortBy,
      sortOrder: sortOptions?.sortOrder,
      ...filters,
    }),
  exportCompanies: (
    communityId: number,
    groupId: UUID,
    filters?: GroupsCompaniesFilters,
    sortOptions?: SortOptions,
    columns?: string[],
  ) => {
    const requestParams = {
      sortBy: sortOptions?.sortBy,
      sortOrder: sortOptions?.sortOrder,
      ...filters,
      columns,
    };
    return api.get<Blob>(`${baseUrl}/communities/${communityId}/groups/${groupId}/companies/export`, {
      requestParams,
      responseType: 'blob',
    });
  },
  listForCompany: (communityId: number, companyId: number) =>
    `${baseUrl}/communities/${communityId}/groups/companies/${companyId}`,
  create: (communityId: number) => `${baseUrl}/communities/${communityId}/groups`,
  view: (communityId: number, groupId: UUID) => `${baseUrl}/communities/${communityId}/groups/${groupId}`,
  update: (communityId: number, groupId: UUID) => `${baseUrl}/communities/${communityId}/groups/${groupId}`,
  delete: (communityId: number, groupId: UUID) => `${baseUrl}/communities/${communityId}/groups/${groupId}`,
  statuses: {
    list: (communityId: number) => `${baseUrl}/communities/${communityId}/groups/statuses`,
    create: (communityId: number) => `${baseUrl}/communities/${communityId}/groups/statuses`,
    update: (communityId: number, statusId: UUID) =>
      `${baseUrl}/communities/${communityId}/groups/statuses/${statusId}`,
    delete: (communityId: number, statusId: UUID) =>
      `${baseUrl}/communities/${communityId}/groups/statuses/${statusId}`,
  },
  updateCompanyStatus: (communityId: number, groupId: UUID, companyId: number) =>
    `${baseUrl}/communities/${communityId}/groups/${groupId}/company-status/${companyId}`,
  updateCompanyGroupMembership: (communityId: number) =>
    `${baseUrl}/communities/${communityId}/groups/company-memberships`,
  comments: {
    list: (communityId: number, groupId: UUID, companyId: number) =>
      `${baseUrl}/communities/${communityId}/groups/${groupId}/companies/${companyId}/comments`,
    create: (communityId: number, groupId: UUID, companyId: number) =>
      `${baseUrl}/communities/${communityId}/groups/${groupId}/companies/${companyId}/comments`,
    update: (communityId: number, groupId: UUID, companyId: number, commentId: UUID) =>
      `${baseUrl}/communities/${communityId}/groups/${groupId}/companies/${companyId}/comments/${commentId}`,
    delete: (communityId: number, groupId: UUID, companyId: number, commentId: UUID) =>
      `${baseUrl}/communities/${communityId}/groups/${groupId}/companies/${companyId}/comments/${commentId}`,
  },
};

export const invalidateCompanyGroups = (communityId: number) =>
  invalidateStartsWith([`${baseUrl}/communities/${communityId}/groups/companies/`]);

export const invalidateCompanyGroupMembership = (communityId: number, companyId: number) =>
  invalidate(groupsUrls.listForCompany(communityId, companyId));

export const invalidateAllGroups = (communityId: number) => {
  invalidateStartsWith([`${baseUrl}/communities/${communityId}/groups/`]);
};
export const useCommunityCompanyGroups = (communityId: number | undefined, companyId: number | undefined) =>
  useResource<CommunityCompanyGroup[]>(
    communityId && companyId ? groupsUrls.listForCompany(communityId, companyId) : undefined,
  );

export const useCommunityGroupsCompanies = (
  communityId: number,
  groupId: UUID,
  filters?: GroupsCompaniesFilters,
  sortOptions?: SortOptions,
  paginationOptions?: PaginationOptions,
) => {
  const getUrl = (options: PaginationOptions) =>
    groupsUrls.listCompanies(communityId, groupId, filters, sortOptions, options);

  const [resource, reload, seeMore] = usePaginatedResource<GroupsCompanyDTO>(
    getUrl,
    paginationOptions || { page: 1, limit: 50 },
    undefined,
    {
      mode: 'append',
      deps: [communityId, groupId, filters, sortOptions],
      uniqueSelector: (company: GroupsCompanyDTO) => company.profile.id,
    },
  );

  return {
    resource,
    reload,
    seeMore,
    invalidateAll: () => {
      // Invalidate all variations of the companies list URLs (including different filters/params)
      const listApplicationCompaniesUrl = `${applicationUrls.listCompanies(communityId).split('?')[0]}`;
      invalidateStartsWith([listApplicationCompaniesUrl]);
      // Force reload of current data
      reload();
    },
  };
};

export const useCommunityGroupStatuses = (communityId: number) =>
  useResource<CommunityGroupStatus[]>(groupsUrls.statuses.list(communityId));

export const useCommunityGroups = (communityId?: number) =>
  useResource<CommunityGroup[]>(communityId ? groupsUrls.list(communityId) : undefined);

export const useCommunityGroupComments = (communityId: number, groupId: UUID, companyId: number) =>
  useResource<CommunityGroupCommentDTO[]>(groupsUrls.comments.list(communityId, groupId, companyId));

export const invalidateGroupsWithCompanies = (communityId: number) =>
  invalidateStartsWith([`${baseUrl}/communities/${communityId}/groups/companies/`]);
export const useGroupWithCompanies = (communityId: number, groupId: UUID) =>
  useResource<CommunityGroupWithCompanies>(groupsUrls.view(communityId, groupId), {
    // Too hard to keep this consistent, so we avoid caching
    cacheDurationMs: 2000,
  });

export const useUpdateCompanyStatus = (communityId: number) => {
  const notify = useNotify();
  return useLazyResource(
    ({ companyId, newStatusId, groupId }: { companyId: number; newStatusId: UUID; groupId: UUID }) =>
      put(groupsUrls.updateCompanyStatus(communityId, groupId, companyId), {
        newStatus: newStatusId,
      }),
    {
      onSuccess: (_, { groupId, companyId }) => {
        // Sync with server
        invalidate(groupsUrls.view(communityId, groupId));
        invalidateCompanyGroups(communityId);
        invalidateStartsWith([groupsUrls.listCompanies(communityId, groupId)]);
        invalidateCompanyGroupMembership(communityId, companyId);
      },
      onFailure: (_, { groupId }) => {
        // If it fails, revert the change
        invalidate(groupsUrls.view(communityId, groupId));
        notify('error', 'Unable move company to new status');
      },
    },
  );
};

export interface CommunityGroup {
  id: UUID;
  name: string;
}

export interface CommunityGroupWithCompanies {
  details: CommunityGroup;
  companies: CompanyWithStatus[];
}

export interface CommunityGroupWithStatus {
  group: CommunityGroup;
  status?: CommunityGroupStatus;
}

export interface CommunityCompanyGroup {
  group: CommunityGroup;
  status?: CommunityGroupStatus;
  commentCount: number;
}

export interface CompanyWithStatus {
  company: TinyCompanyDTO;
  status?: CommunityGroupStatus;
}

export interface NewCommunityGroup {
  name: string;
}

export interface UpdateCommunityGroup {
  name: string;
}

export interface CommunityGroupStatus {
  id: UUID;
  name: string;
  color: string;
}

export interface NewCommunityGroupStatus {
  name: string;
  color: string;
}

export interface UpdateCommunityGroupStatus {
  name?: string;
  color?: string;
  sortOrder?: number;
}

export interface UpdateCompanyStatus {
  newStatus: UUID | null;
}

export interface GroupsCompanyDTO {
  profile: TinyCompanyDTO;
  labels: CompactLabelDTO[];
  fundingStage: CompactLabelDTO | null;
  stages: CompactLabelDTO[];
  industries: CompactLabelDTO[];
  companyAdmin?: UserProfile;
  joinedCommunityDate: DateString;
  submittedAt?: DateString;
  location?: Location;
  completenessPercentage?: number;
  completedParts?: CompletedPartDTO[];
  status?: LabelDTO;
  averageRating?: number;
}

export interface CompletedPartDTO {
  name: string;
  isCompleted: boolean;
}

// Group Comments DTOs
export interface CommunityGroupCommentDTO {
  id: UUID;
  companyId: number;
  communityId: number;
  groupId?: UUID;
  comment: string;
  commentedBy: TinyUser;
  updatedAt?: DateString;
  deletedAt?: DateString;
  createdAt: DateString;
}

export interface AddGroupCommentDTO {
  comment: string;
}

export interface UpdateGroupCommentDTO {
  message: string;
}
